import { useState, useEffect, useCallback } from 'react'
import { DashboardMetrics, ActivityFeedItem } from '../types/api'
import { adminApi } from '../lib/sdkClient'

export function useRealTimeData() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchMetrics = useCallback(async () => {
    try {
      const { data } = await adminApi.adminOverviewStatsArtrackAdminStatsOverviewGet()
      // Map SDK AdminOverview -> local DashboardMetrics
      const mapped: DashboardMetrics = {
        active_users_24h: (data as any)?.active_users_24h ?? 0,
        total_tracks: (data as any)?.total_tracks ?? 0,
        tracks_today: (data as any)?.tracks_today ?? 0,
        total_waypoints: (data as any)?.total_waypoints ?? 0,
        waypoints_today: (data as any)?.waypoints_today ?? 0,
        storage_used_bytes: (data as any)?.storage_used_bytes ?? 0,
        storage_used_mb: Math.round((((data as any)?.storage_used_bytes ?? 0) / (1024 * 1024))),
        processing_queue_count: (data as any)?.processing_queue_count ?? 0,
        error_rate_24h: 0,
        last_updated: (data as any)?.last_updated ?? new Date().toISOString(),
      }
      setMetrics(mapped)
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch metrics')
      console.error('Error fetching metrics:', err)
    }
  }, [])

  const fetchActivityFeed = useCallback(async () => {
    try {
      const { data } = await adminApi.adminActivityFeedArtrackAdminActivityFeedGet(50)
      const allowed = new Set(['waypoint_created','track_created','user_registered','analysis_completed'])
      const mapped: ActivityFeedItem[] = (data as any[]).map((it: any) => ({
        id: String(it.id),
        timestamp: String(it.timestamp),
        type: allowed.has(it.type) ? it.type : 'analysis_completed',
        user: {
          id: String(it.user?.id ?? ''),
          display_name: String(it.user?.display_name ?? ''),
        },
        track: it.track ? { id: String(it.track.id), name: String(it.track.name ?? '') } : undefined,
        details: (it.details ?? {}) as Record<string, any>,
      }))
      setActivityFeed(mapped)
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch activity feed')
      console.error('Error fetching activity feed:', err)
    }
  }, [])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    await Promise.all([fetchMetrics(), fetchActivityFeed()])
    setLoading(false)
  }, [fetchMetrics, fetchActivityFeed])

  useEffect(() => {
    // Initial load
    fetchAllData()

    // Set up intervals for real-time updates
    const metricsInterval = setInterval(fetchMetrics, 30000) // 30 seconds
    const activityInterval = setInterval(fetchActivityFeed, 10000) // 10 seconds

    return () => {
      clearInterval(metricsInterval)
      clearInterval(activityInterval)
    }
  }, [fetchAllData, fetchMetrics, fetchActivityFeed])

  return {
    metrics,
    activityFeed,
    loading,
    error,
    refresh: fetchAllData,
  }
}